import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  ITicketSoporteCorporativo,
  ITicketSoporteCorporativoPagingRes,
  IDocumentoTicket,
  IAuditoriaTicketSoporteCorporativoPagingRes,
} from '@/shared/interfaces/erp/soporte-corporativo';
import { useUiStore } from '@/store/ui';
import { useAuthStore } from '@/store/auth';
import axios from 'axios';

const { get, post, put } = erpApi();

// Response type for upload
export interface IUploadDocumentosResponse {
  code: number;
  status: string;
  message: string;
  data: {
    documentos_subidos: IDocumentoTicket[];
    errores: string[];
    total_documentos: number;
  };
}

///* Params types
export type GetTicketSoporteCorporativoParams = {
  page?: number;
  page_size?: number;
  ruc?: string;
  razon_social?: string;
  numero_contrato?: string;
  nombre_enlace?: string;
  tipo_ticket?: string;
  estado_ticket?: string;
  tipo_cliente?: string;
  fecha_ejecucion_after?: string;
  fecha_ejecucion_before?: string;
  numero_referencia?: string;
};

export type CreateTicketSoporteCorporativoParams = Omit<
  ITicketSoporteCorporativo,
  'id_ticket_soporte' | 'codigo' | 'numero_referencia' | 'fecha_creacion' | 'fecha_modificacion'
>;

export type UpdateTicketSoporteCorporativoParams = {
  id: string;
  data: Partial<CreateTicketSoporteCorporativoParams>;
};

///* React Query Hooks

// Fetch paginated tickets
export const useFetchTicketsSoporteCorporativo = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetTicketSoporteCorporativoParams>) => {
  return useQuery({
    queryKey: ['ticket-soporte-corporativo', params],
    queryFn: async () => {
      return getTicketsSoporteCorporativo(params);
    },
    enabled,
  });
};

// Fetch single ticket by ID
export const useGetTicketSoporteCorporativo = (id: string) => {
  return useQuery({
    queryKey: ['ticket-soporte-corporativo', id],
    queryFn: async () => {
      return getTicketSoporteCorporativo(id);
    },
    enabled: !!id,
  });
};

// Create ticket
export const useCreateTicketSoporteCorporativo = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTicketSoporteCorporativo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-soporte-corporativo'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ticket creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

// Update ticket
export const useUpdateTicketSoporteCorporativo = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTicketSoporteCorporativo,
    onSuccess: async () => {
      // Forzar refetch inmediato de todas las queries de tickets activas
      await queryClient.refetchQueries({
        queryKey: ['ticket-soporte-corporativo'],
        type: 'active'
      });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);
      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ticket actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* Axios functions

export const getTicketsSoporteCorporativo = async (
  params?: GetTicketSoporteCorporativoParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<ITicketSoporteCorporativoPagingRes>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/?${queryParams}`,
    true
  );
};

export const getTicketSoporteCorporativo = async (id: string) => {
  try {
    return await get<ITicketSoporteCorporativo>(
      `${urlBackend}api/v1/ticket-soporte-corporativo/${id}/`,
      true
    );
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createTicketSoporteCorporativo = async (
  data: CreateTicketSoporteCorporativoParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<ITicketSoporteCorporativo>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/`,
    data,
    true
  );
};

export const updateTicketSoporteCorporativo = async ({
  id,
  data,
}: UpdateTicketSoporteCorporativoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<ITicketSoporteCorporativo>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/${id}/`,
    data,
    true
  );
};

///* Upload documents functions

export type UploadDocumentosParams = {
  ticketId: string;
  files: File[];
};

export type DeleteDocumentoParams = {
  ticketId: string;
  url: string;
};

// Upload documents to a ticket
export const uploadDocumentosTicket = async ({
  ticketId,
  files,
}: UploadDocumentosParams): Promise<IUploadDocumentosResponse> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  try {
    const response = await axios.post<IUploadDocumentosResponse>(
      `${urlBackend}api/v1/ticket-soporte-corporativo/${ticketId}/documentos/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${token}`,
        },
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Delete a document from a ticket
export const deleteDocumentoTicket = async ({
  ticketId,
  url,
}: DeleteDocumentoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  try {
    const response = await axios.delete(
      `${urlBackend}api/v1/ticket-soporte-corporativo/${ticketId}/documentos/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
        data: { url },
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Hook for uploading documents
export const useUploadDocumentosTicket = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: uploadDocumentosTicket,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-soporte-corporativo'] });
      setAlert &&
        setAlert(
          true,
          data.message || 'Documentos subidos correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al subir documentos');
    },
  });
};

// Hook for deleting a document
export const useDeleteDocumentoTicket = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: deleteDocumentoTicket,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket-soporte-corporativo'] });
      setAlert &&
        setAlert(true, 'Documento eliminado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al eliminar documento');
    },
  });
};

///* Export Excel functions

export type ExportExcelParams = {
  ticket_ids?: string[];
  fecha_inicio?: string;
  fecha_fin?: string;
  numero_referencia?: string;
};

// Export tickets to Excel
export const exportTicketsToExcel = async (params: ExportExcelParams): Promise<Blob> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  try {
    const response = await axios.post(
      `${urlBackend}api/v1/ticket-soporte-corporativo/export-excel/`,
      params,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Hook for exporting tickets to Excel
export const useExportTicketsToExcel = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: exportTicketsToExcel,
    onSuccess: (data) => {
      // Crear URL del blob y descargar
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_soporte_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setAlert &&
        setAlert(true, 'Excel exportado correctamente', AlertSeverityEnum.success);
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al exportar Excel');
    },
  });
};

///* Auditoría functions

export type GetAuditoriaTicketSoporteParams = {
  page?: number;
  page_size?: number;
  ticket?: string;
  numero_ticket?: string;
  tipo_accion?: string;
  username?: string;
  ip?: string;
  fecha_after?: string;
  fecha_before?: string;
};

// Fetch paginated audit records
export const useFetchAuditoriaTicketSoporte = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<GetAuditoriaTicketSoporteParams>) => {
  return useQuery({
    queryKey: ['auditoria-ticket-soporte', params],
    queryFn: async () => {
      return getAuditoriaTicketSoporte(params);
    },
    enabled,
  });
};

// Axios function for audit records
export const getAuditoriaTicketSoporte = async (
  params?: GetAuditoriaTicketSoporteParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<IAuditoriaTicketSoporteCorporativoPagingRes>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/auditoria/?${queryParams}`,
    true
  );
};

///* Rechazar ticket functions

export type RechazarTicketParams = {
  ticketId: string;
  motivo_rechazo: string;
};

export interface IRechazarTicketResponse {
  code: number;
  status: string;
  message: string;
  data: ITicketSoporteCorporativo;
}

// Rechazar un ticket
export const rechazarTicketSoporteCorporativo = async ({
  ticketId,
  motivo_rechazo,
}: RechazarTicketParams): Promise<IRechazarTicketResponse> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  try {
    const response = await axios.post<IRechazarTicketResponse>(
      `${urlBackend}api/v1/ticket-soporte-corporativo/${ticketId}/rechazar/`,
      { motivo_rechazo },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Hook for rejecting a ticket
export const useRechazarTicketSoporteCorporativo = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: rechazarTicketSoporteCorporativo,
    onSuccess: async (response) => {
      // Forzar refetch inmediato de todas las queries de tickets activas
      await queryClient.refetchQueries({
        queryKey: ['ticket-soporte-corporativo'],
        type: 'active'
      });

      setAlert &&
        setAlert(
          true,
          response.message || 'Ticket rechazado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al rechazar el ticket');
    },
  });
};

///* Correos en copia functions

export type UpdateCorreosCopiaParams = {
  ticketId: string;
  correos_copia: string[];
};

export interface ICorreosCopiaResponse {
  code: number;
  status: string;
  message: string;
  data: ITicketSoporteCorporativo;
}

export interface IGetCorreosCopiaResponse {
  code: number;
  status: string;
  data: {
    correo_tecnico: string | null;
    correos_copia: string[];
  };
}

// Obtener correos en copia de un ticket
export const getCorreosCopiaTicket = async (ticketId: string): Promise<IGetCorreosCopiaResponse> => {
  const token = useAuthStore.getState().token;

  const response = await axios.get<IGetCorreosCopiaResponse>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/${ticketId}/correos-copia/`,
    {
      headers: {
        Authorization: `Token ${token}`,
      },
    }
  );
  return response.data;
};

// Actualizar correos en copia de un ticket
export const updateCorreosCopiaTicket = async ({
  ticketId,
  correos_copia,
}: UpdateCorreosCopiaParams): Promise<ICorreosCopiaResponse> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  try {
    const response = await axios.put<ICorreosCopiaResponse>(
      `${urlBackend}api/v1/ticket-soporte-corporativo/${ticketId}/correos-copia/`,
      { correos_copia },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Hook for getting correos copia
export const useFetchCorreosCopiaTicket = (ticketId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['correos-copia-ticket', ticketId],
    queryFn: () => getCorreosCopiaTicket(ticketId!),
    enabled: enabled && !!ticketId,
  });
};

// Hook for updating correos copia
export const useUpdateCorreosCopiaTicket = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCorreosCopiaTicket,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-soporte-corporativo'] });
      queryClient.invalidateQueries({ queryKey: ['correos-copia-ticket'] });
      setAlert &&
        setAlert(
          true,
          data.message || 'Correos en copia actualizados correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al actualizar los correos en copia');
    },
  });
};

///* Correos predeterminados functions

export type UpdateCorreosPredeterminadosParams = {
  correos_predeterminados: string[];
};

export interface ICorreosPredeterminadosResponse {
  code: number;
  status: string;
  message: string;
}

export interface IGetCorreosPredeterminadosResponse {
  code: number;
  status: string;
  data: {
    correos_predeterminados: string[];
  };
}

// Obtener correos predeterminados
export const getCorreosPredeterminados = async (): Promise<IGetCorreosPredeterminadosResponse> => {
  const token = useAuthStore.getState().token;

  const response = await axios.get<IGetCorreosPredeterminadosResponse>(
    `${urlBackend}api/v1/ticket-soporte-corporativo/configuracion/correos-predeterminados/`,
    {
      headers: {
        Authorization: `Token ${token}`,
      },
    }
  );
  return response.data;
};

// Actualizar correos predeterminados
export const updateCorreosPredeterminados = async ({
  correos_predeterminados,
}: UpdateCorreosPredeterminadosParams): Promise<ICorreosPredeterminadosResponse> => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const token = useAuthStore.getState().token;
  setIsGlobalLoading(true);

  try {
    const response = await axios.put<ICorreosPredeterminadosResponse>(
      `${urlBackend}api/v1/ticket-soporte-corporativo/configuracion/correos-predeterminados/`,
      { correos_predeterminados },
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } finally {
    setIsGlobalLoading(false);
  }
};

// Hook for getting correos predeterminados
export const useFetchCorreosPredeterminados = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['correos-predeterminados-tickets'],
    queryFn: getCorreosPredeterminados,
    enabled,
  });
};

// Hook for updating correos predeterminados
export const useUpdateCorreosPredeterminados = () => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCorreosPredeterminados,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['correos-predeterminados-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['correos-copia-ticket'] });
      setAlert &&
        setAlert(
          true,
          data.message || 'Correos predeterminados actualizados correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: (error) => {
      handleAxiosError(error, 'Error al actualizar los correos predeterminados');
    },
  });
};
